import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';

const ClientContactSubmissions = () => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  // Get client profile first to get the client ID
  const { data: clientProfileData, isLoading: profileLoading } = useQuery(
    ['clientProfile'],
    async () => {
      const response = await axios.get('/clients/my-profile');
      return response.data;
    }
  );

  const clientData = clientProfileData?.client;
  const clientId = clientData?._id;

  const { data: submissionsData, isLoading, refetch } = useQuery(
    ['contactSubmissions', clientId, currentPage, statusFilter],
    async () => {
      const response = await axios.get(
        `/contact/submissions/${clientId}?page=${currentPage}&limit=10&status=${statusFilter}`
      );
      return response.data;
    },
    {
      enabled: !!clientId
    }
  );

  const submissions = submissionsData?.submissions || [];
  const pagination = submissionsData?.pagination || {};
  const statusCounts = submissionsData?.statusCounts || {};

  const updateSubmissionStatus = async (submissionId, newStatus) => {
    try {
      await axios.patch(`/contact/submissions/${submissionId}/status`, {
        status: newStatus
      });
      // Refresh the list
      refetch();
    } catch (error) {
      console.error('Error updating submission status:', error);
    }
  };

  const { refetch: refetchClientProfile } = useQuery(
    ['clientProfile'],
    async () => {
      const response = await axios.get('/clients/my-profile');
      return response.data;
    }
  );

  const handleContactFormToggle = async (e) => {
    try {
      const showContactForm = e.target.checked;
      await axios.put('/clients/my-settings', {
        chatbotConfig: {
          ...clientData?.chatbotConfig,
          showContactForm
        }
      });
      toast.success('Contact form setting updated successfully');
      refetchClientProfile();
    } catch (error) {
      toast.error('Failed to update contact form setting');
    }
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('lt-LT');
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': 'bg-blue-100 text-blue-800',
      'read': 'bg-yellow-100 text-yellow-800',
      'replied': 'bg-green-100 text-green-800',
      'closed': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusText = (status) => {
    const texts = {
      'new': 'Nauja',
      'read': 'Perskaityta',
      'replied': 'Atsakyta',
      'closed': 'Uždaryta'
    };
    return texts[status] || status;
  };

  if (isLoading || profileLoading) {
    return <LoadingSpinner />;
  }
  
  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Contact Submissions</h1>
            <p className="text-gray-600">Manage contact form submissions from your website visitors</p>
          </div>
        </div>
      </div>

      {/* Contact Form Setting */}
      <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
        <label className="flex items-start">
          <input
            type="checkbox"
            checked={clientData?.chatbotConfig?.showContactForm || false}
            onChange={handleContactFormToggle}
            className="mt-1 mr-3 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
          />
          <div>
            <span className="text-sm font-medium text-green-800">
              Enable Contact Form
            </span>
            <p className="text-sm text-green-700 mt-1">
              When enabled, visitors to your website will see a contact form button that allows them to send you their contact details and messages directly.
            </p>
          </div>
        </label>
      </div>
        
      {/* Status Filter Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {[
              { key: 'all', label: 'All', count: Object.values(statusCounts).reduce((a, b) => a + b, 0) },
              { key: 'new', label: 'New', count: statusCounts.new || 0 },
              { key: 'read', label: 'Read', count: statusCounts.read || 0 },
              { key: 'replied', label: 'Replied', count: statusCounts.replied || 0 },
              { key: 'closed', label: 'Closed', count: statusCounts.closed || 0 }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => handleStatusFilter(tab.key)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  statusFilter === tab.key
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Submissions List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        {submissions.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No contact form submissions found</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {submissions.map((submission) => (
              <div key={submission._id} className="p-6 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <h3 className="text-lg font-medium text-gray-900 mr-3">
                        {submission.name}
                      </h3>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(submission.status)}`}>
                        {getStatusText(submission.status)}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-gray-500">Email</p>
                        <p className="text-sm text-gray-900">{submission.email}</p>
                      </div>
                      {submission.phone && (
                        <div>
                          <p className="text-sm text-gray-500">Phone</p>
                          <p className="text-sm text-gray-900">{submission.phone}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-sm text-gray-500">Submitted</p>
                        <p className="text-sm text-gray-900">{formatDate(submission.submittedAt)}</p>
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <p className="text-sm text-gray-500 mb-1">Message</p>
                      <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded">
                        {submission.message}
                      </p>
                    </div>

                    {submission.sourceUrl && submission.sourceUrl !== 'Unknown' && (
                      <div className="mb-4">
                        <p className="text-sm text-gray-500">Source Page</p>
                        <p className="text-xs text-blue-600 truncate">{submission.sourceUrl}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="ml-6 flex flex-col space-y-2">
                    <select
                      value={submission.status}
                      onChange={(e) => updateSubmissionStatus(submission._id, e.target.value)}
                      className="text-sm border border-gray-300 rounded px-3 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="new">New</option>
                      <option value="read">Read</option>
                      <option value="replied">Replied</option>
                      <option value="closed">Closed</option>
                    </select>
                    
                    <a
                      href={`mailto:${submission.email}?subject=Re: Your inquiry&body=Hello ${submission.name},%0D%0A%0D%0AThank you for your inquiry...`}
                      className="text-sm bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-700 text-center"
                    >
                      Reply
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="mt-6 flex justify-center">
          <nav className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            
            <span className="px-3 py-2 text-sm font-medium text-gray-700">
              {currentPage} of {pagination.pages}
            </span>
            
            <button
              onClick={() => setCurrentPage(Math.min(pagination.pages, currentPage + 1))}
              disabled={currentPage === pagination.pages}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </nav>
        </div>
      )}
    </div>
  );
};

export default ClientContactSubmissions;